import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Mail } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { AuthLayout } from '@/components/layout/AuthLayout';
import { useT } from '@/i18n/LanguageContext';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const t = useT();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) {
      setError(t.forgotPassword.emailRequired);
      return;
    }

    setLoading(true);
    try {
      await apiFetch('/api/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email: email.trim() }),
      });
      setSent(true);
    } catch {
      // Always show success (anti-enumeration)
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  const emailSentDescParts = t.forgotPassword.emailSentDesc.split('{email}');

  return (
    <AuthLayout>
      <p className="text-sm font-semibold uppercase tracking-[0.2em] text-[#523EF7] mb-4">
        {t.common.appName}
      </p>

      <h1 className="text-3xl sm:text-4xl font-bold text-[#222222] tracking-tight leading-tight mb-2">
        {t.forgotPassword.title}
      </h1>
      <p className="text-[#9A9A9A] text-base mb-8">
        {sent ? t.forgotPassword.subtitleSent : t.forgotPassword.subtitleDefault}
      </p>

      <div className="max-w-sm mx-auto">
        {sent ? (
          <div className="text-center space-y-4">
            <div className="w-14 h-14 mx-auto bg-emerald-50 rounded-full flex items-center justify-center">
              <Mail size={24} className="text-emerald-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-[#222222]">{t.forgotPassword.emailSent}</p>
              <p className="text-sm text-[#9A9A9A] mt-1">
                {emailSentDescParts[0]}<strong className="text-[#3F3F3F]">{email}</strong>{emailSentDescParts[1]}
              </p>
            </div>
            <Link
              to="/login"
              className="inline-flex items-center gap-1 text-sm text-[#9A9A9A] hover:text-[#523EF7] transition-colors"
            >
              <ArrowLeft size={14} />
              {t.common.backToLogin}
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 text-left">
            <div className="flex flex-col gap-1">
              <label htmlFor="email" className="text-sm font-medium text-[#3F3F3F]">
                {t.common.email} <span className="text-red-500">*</span>
              </label>
              <input
                id="email"
                type="email"
                placeholder={t.forgotPassword.emailPlaceholder}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                className="auth-input"
              />
            </div>

            {error && (
              <p className="text-sm text-red-600 bg-red-50 rounded-xl px-3 py-2">{error}</p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="auth-btn-primary w-full"
            >
              {loading ? (
                <svg className="animate-spin h-5 w-5 mx-auto" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              ) : (
                t.forgotPassword.submit
              )}
            </button>

            <div className="text-center pt-1">
              <Link
                to="/login"
                className="inline-flex items-center gap-1 text-sm text-[#9A9A9A] hover:text-[#523EF7] transition-colors"
              >
                <ArrowLeft size={14} />
                {t.common.backToLogin}
              </Link>
            </div>
          </form>
        )}
      </div>
    </AuthLayout>
  );
}
